import React, {MutableRefObject} from 'react';
import {
    Card,
    GroupContainer,
    Space,
    List,
    Row,
    Col,
    ListItem,
    Button,
    Icon,
    ButtonToolbar,
    Menu,
    MenuItem,
    Text,
    MenuDivider,
    Title,
    NoData,
    showConfirm,
    Modal,
    Tag,
    showInfo,
    Tooltip,
    toaster,
    LabelValue,
} from '@mc/ui-v4';
import {
    AtributyApi,
    AttributeConfigVO,
    AkceApi,
    ActionConfigVO,
    EnumActionType,
    EnumInterpretace,
    LayerSettingsVO,
    LayerSettingsApi,
    RightsApi,
    UserRightsVO,
    LayerVO,
    LayerDataUpdatedVO,
    UserLayersApi,
} from '../../../api';
import {mcDelete, mcEdit, mcFilterOutline, mcGlobe, mcLocation, mcMoveDown, mcMoveUp, mcNew, mcSearch} from '@mc/react-ui-icons';
import {OpravneniConfiguration} from './OpravneniConfiguration';
import {CreateUpdateAtributModal} from './CreateUpdateAtributModal';
import {getEnumTypLayerAttrReadable} from '../../../enums/EnumTypLayerAttr';
import {UserLayerModal} from './UserLayerModal';
import {actionApiCall, rffFormSubmit, WaitForQueryData} from '@mc/react-app-v4';
import {UpravaAkceModal} from './UpravaAkceModal';
import {QueryClient, useQuery, useQueryClient} from 'react-query';
import {
    CACHE_KEY_AKCE,
    CACHE_KEY_ATRIBUTY,
    CACHE_KEY_LAYER_SETTING,
    CACHE_KEY_LAYER_OPRAVNENI,
    CACHE_KEY_USER_LAYER_OPRAVNENI,
    CACHE_KEY_LAYER_TOPIC,
    CACHE_KEY_LAYER_ATTRIBUTES,
    CACHE_KEY_LAYER_DATA_UPDATED,
} from '../../../queryKeys';
import {CACHE_KEY_LAYERS_CONFIG} from '../../../hooks/useLayersConfig';
import {CACHE_KEY_TOPICS_MAP} from '../../../hooks/useTopicsMap';
import {CACHE_KEY_DISCRIMINATOR_LAYERS} from '../../../hooks/useDiscriminatorLayers';
import {useAppConfig} from '@mc/react-app-base';
import {UserLayerAttributeSymbols} from './UserLayerAttributeSymbols';
import {ApiCallWaitingModal} from '../ApiCallWaitingModal';
import {MapEditorActions} from '@mc/react-mapio';
import {isLayerVlastnik, isUserAdminOrMapioAdmin} from '../../../helpers/AuthRights';
import {useAuth} from '@mc/react-auth';
import {DefaultValueModal} from './DefaultValueModal';
import {formatDate} from '../../../helpers/Formatter';

/**
 * Může se z atributu stát diskriminátor (odlišení symbolů podle hodnot atributu)? Podřízená vrstva
 * vzniká pro jednu hodnotu atributu, u vícehodnotového číselníku by objekt patřil do několika vrstev
 * zároveň – server takový diskriminátor odmítne, nenabízíme ho proto ani v menu (MCW-29258).
 */
const muzeBytDiskriminator = (atribut: AttributeConfigVO): boolean => atribut.interpretace !== EnumInterpretace.MULTI_CISELNIK;

type ComponentProps = {layer: LayerVO; topicId: string; mapRef: MutableRefObject<MapEditorActions>};
export const UserLayerConfiguration: React.FC<ComponentProps> = ({layer, topicId, mapRef}) => {
    const {data: auth} = useAuth();

    const queryClient: QueryClient = useQueryClient();
    const appConfig = useAppConfig();

    //------------------------- METODY ATRIBUTŮ -------------------------
    const handleCreateUpdateAtribut = (forbiddenAttributes: string[], initialValues?: AttributeConfigVO) => {
        const isEdit = !!initialValues;
        Modal.show(({onClose}) => (
            <CreateUpdateAtributModal
                forbiddenAttributes={forbiddenAttributes.filter(i => initialValues?.popisek !== i)}
                initialValues={initialValues}
                layerId={layer.id}
                isEdit={isEdit}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        if (isEdit) {
                            await AtributyApi.updateAtribut(data.id, layer.id, data);
                        } else {
                            await AtributyApi.createAtribut(layer.id, data);
                        }
                        await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_AKCE, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
                        onClose();
                    });
                }}
                onClose={onClose}
                xsdTyp={layer.xsdTyp}
            />
        ));
    };
    const removeAtributAndRefresh = async (atribut: AttributeConfigVO) => {
        await actionApiCall(() => AtributyApi.removeAtribut(layer.id, atribut.id));
        await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
        await queryClient.invalidateQueries([CACHE_KEY_AKCE, layer.id]);
        await queryClient.invalidateQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
        await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
    };

    const handleRemoveAtribut = async (atribut: AttributeConfigVO) => {
        if (atribut.typ === 'ENUM') {
            const response = await AtributyApi.existsDataForCiselnikyAtributu(atribut.id);
            if (response.data) {
                await showInfo({
                    title: 'Odstranit číselník',
                    message:
                        'Nelze smazat číselník, protože je použit u existujících objektů. Před smazáním je potřeba zajistit, aby číselník nebyl nikde použit.',
                    confirmText: 'Zavřít',
                });
                return;
            }
        }
        const result = await showConfirm({
            title: 'Odstranit atribut',
            message: 'Opravdu chcete smazat atribut?',
            intent: 'danger',
            cancelText: 'Zrušit',
            confirmText: 'Smazat',
        });
        if (!result) {
            return;
        }
        // Pokud by po smazání vrstva zůstala bez atributu použitého jako název, musí se zbývajícímu
        // prvnímu atributu nastavit Povinný a Použít atribut jako název, aby nešlo obejít logiku prvního atributu
        const zbyvajiciAtributy = (useAtributyQuery.data ?? []).filter(a => a.id !== atribut.id).sort((a, b) => (a.poradi ?? 0) - (b.poradi ?? 0));
        const novyLabelAtribut = zbyvajiciAtributy.length > 0 && !zbyvajiciAtributy.some(a => a.pouzitJakoLabel) ? zbyvajiciAtributy[0] : undefined;
        if (novyLabelAtribut) {
            const povinnyResult = await showConfirm({
                title: 'Povinný atribut',
                message: (
                    <p>
                        Chcete nastavit atribut <strong>{novyLabelAtribut.nazev}</strong> jako povinný? Dojde k naplnění výchozí hodnotou pro všechny existující
                        prvky, které ji nemají vyplněnou.
                    </p>
                ),
                intent: 'danger',
            });
            if (!povinnyResult) {
                return;
            }
            if (!novyLabelAtribut.defaultniHodnota) {
                const layerHasData = await UserLayersApi.getFeatureIds(layer.id);
                if (layerHasData && layerHasData.data?.length > 0) {
                    // Vrstva obsahuje data a zbývající atribut nemá výchozí hodnotu - vyžádáme si ji od uživatele
                    Modal.show(({onClose}) => (
                        <DefaultValueModal
                            onClose={onClose}
                            atribut={novyLabelAtribut}
                            onSubmit={data => {
                                return rffFormSubmit(async () => {
                                    await AtributyApi.updateAtribut(novyLabelAtribut.id, layer.id, {
                                        ...novyLabelAtribut,
                                        povinne: true,
                                        pouzitJakoLabel: true,
                                        defaultniHodnota: data.hodnota,
                                    });
                                    await removeAtributAndRefresh(atribut);
                                    onClose();
                                });
                            }}
                        />
                    ));
                    return;
                }
            }
        }
        await removeAtributAndRefresh(atribut);
    };

    const handleZrusitOdliseniPodleAtributu = async (atribut: AttributeConfigVO) => {
        const result = await showConfirm({
            title: 'Vypnout rozlišení podle atributu?',
            message: 'Vypnutím rozlišení podle atributu dojde k odstranení konfigurace symbolů',
            intent: 'danger',
        });
        if (result) {
            await Modal.showWaiting(
                actionApiCall(async () => await AtributyApi.changeDiscriminator(atribut.id, false)),
                ApiCallWaitingModal,
            );
            queryClient.removeQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
        }
    };

    const handleZapnoutOdliseniPodleAtributu = async (atribut: AttributeConfigVO) => {
        const layerHasData = await UserLayersApi.getFeatureIds(layer.id);
        if (layerHasData && layerHasData.data?.length > 0) {
            const result = await showConfirm({
                title: 'Nastavit výchozí hodnotu atributu',
                message: (
                    <span>
                        Vrstva obsahuje data bez nastavené hodnoty tohoto atributu. <br />
                        Pro zapnutí rozlišení symbolů je nutné nejprve přiřadit výchozí hodnotu všem objektům.
                    </span>
                ),
                intent: 'danger',
            });
            if (result) {
                Modal.show(({onClose}) => (
                    <DefaultValueModal
                        onClose={onClose}
                        atribut={atribut}
                        onSubmit={async data => {
                            return rffFormSubmit(async () => {
                                await Modal.showWaiting(
                                    actionApiCall(async () => {
                                        await AtributyApi.changeDiscriminator(atribut.id, true, data.hodnota);
                                        queryClient.removeQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
                                        await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
                                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
                                    }),
                                    ApiCallWaitingModal,
                                );
                                onClose();
                            });
                        }}
                    />
                ));
            } else return;
        } else {
            const result = await showConfirm({
                title: 'Zapnout rozlišení podle atributu?',
                message: 'Zapnutím rozlišení podle atributu dojde k odstranění původní konfigurace symbolů',
                intent: 'danger',
            });
            if (result) {
                await Modal.showWaiting(
                    actionApiCall(async () => await AtributyApi.changeDiscriminator(atribut.id, true)),
                    ApiCallWaitingModal,
                );
                await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
                await queryClient.invalidateQueries([CACHE_KEY_DISCRIMINATOR_LAYERS, layer.id]);
                await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
            }
        }
    };

    const moveAtribut = async (atribut: AttributeConfigVO, resultIndex) => {
        await actionApiCall(() => AtributyApi.moveAtribut(layer.id, atribut.id, resultIndex));
        await queryClient.invalidateQueries([CACHE_KEY_ATRIBUTY, layer.id]);
    };

    //------------------------- METODY AKCÍ -------------------------
    const handleCreateUpdateAkce = (initialValues: ActionConfigVO, isEdit: boolean, atributy: AttributeConfigVO[], diskriminatorAtribut: AttributeConfigVO) => {
        Modal.show(({onClose}) => (
            <UpravaAkceModal
                initialValues={initialValues}
                diskriminatorAtribut={diskriminatorAtribut}
                isEdit={isEdit}
                atributy={atributy}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        if (data.vychozi) {
                            const akceData: ActionConfigVO[] = useAkceQuery.data;
                            const defaultAction = akceData.find(action => action.vychozi === true);
                            if (defaultAction) {
                                defaultAction.vychozi = false;
                                await AkceApi.updateAction(defaultAction.id, layer.id, defaultAction);
                            }
                        }
                        if (isEdit) {
                            await AkceApi.updateAction(data.id, layer.id, data);
                        } else {
                            await AkceApi.createAction(layer.id, data);
                        }
                        await queryClient.invalidateQueries([CACHE_KEY_AKCE, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_OPRAVNENI, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_USER_LAYER_OPRAVNENI, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
                        onClose();
                    });
                }}
                onClose={onClose}
            />
        ));
    };
    const handleRemoveAkce = async (akce: any) => {
        const result = await showConfirm({
            title: 'Odstranit akci',
            message: 'Opravdu chcete smazat akci?',
            intent: 'danger',
        });
        if (result) {
            await actionApiCall(() => AkceApi.deleteAction(layer.id, akce.id));
            await queryClient.invalidateQueries([CACHE_KEY_AKCE, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_OPRAVNENI, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_USER_LAYER_OPRAVNENI, layer.id]);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_ATTRIBUTES, layer.id]);
        }
    };

    //------------------------- METODY VRSTVY -------------------------
    const handleUpdateLayer = (initialValues: LayerSettingsVO) => {
        const {barva, ...other} = initialValues;

        Modal.show(({onClose}) => (
            <UserLayerModal
                initialValues={{...other, barva: barva ? barva.replace('#', '') : undefined}}
                onClose={onClose}
                isEdit={true}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        await LayerSettingsApi.updateLayer(data.id, data);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_SETTING, layer.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, topicId]);
                        mapRef.current?.updateTopic(topicId);
                        onClose();
                    });
                }}
                xsdTyp={layer.xsdTyp}
            />
        ));
    };

    /**
     * Spustí publikaci vrstvy do veřejné instance Mapia. Publikace běží na serveru na pozadí,
     * uživatel na její dokončení nečeká.
     */
    const handlePublishLayer = async () => {
        const result = await showConfirm({
            title: 'Publikovat vrstvu',
            message: 'Opravdu spustit publikaci vrstvy?',
            cancelText: 'Zrušit',
            confirmText: 'Publikovat',
        });
        if (!result) {
            return;
        }
        await actionApiCall(() => LayerSettingsApi.publishLayer(layer.id));
        toaster.success('Publikace vrstvy byla spuštěna, běží na pozadí. Datum aktualizace dat se změní po jejím dokončení.');
    };

    const useLayerSettingQuery = useQuery<LayerSettingsVO>(
        [CACHE_KEY_LAYER_SETTING, layer?.id],
        async () => (await LayerSettingsApi.getLayer(layer?.id)).data,
        {enabled: !!layer?.id},
    );
    const useAtributyQuery = useQuery<AttributeConfigVO[]>([CACHE_KEY_ATRIBUTY, layer?.id], async () => (await AtributyApi.getAllAtribut(layer?.id)).data, {
        enabled: !!layer?.id,
    });
    const useAkceQuery = useQuery<ActionConfigVO[]>([CACHE_KEY_AKCE, layer?.id], async () => (await AkceApi.getAllActions(layer?.id)).data, {
        enabled: !!layer?.id,
    });
    const useOpravneniQuery = useQuery<UserRightsVO[]>(
        [CACHE_KEY_LAYER_OPRAVNENI, layer?.id],
        async () => (await RightsApi.getAllLayerRights(layer?.id)).data,
        {enabled: !!layer?.id},
    );
    // Datum aktualizace dat je jen doplňková informace v hlavičce, na její načtení detail vrstvy nečeká
    const useDataUpdatedQuery = useQuery<LayerDataUpdatedVO>(
        [CACHE_KEY_LAYER_DATA_UPDATED, layer?.id],
        async () => (await LayerSettingsApi.getLayerDataUpdated(layer?.id)).data,
        {enabled: !!layer?.id},
    );

    return (
        <>
            {!layer ? (
                <NoData>Vrstvu se nepodařilo najít</NoData>
            ) : (
                <WaitForQueryData queries={[useLayerSettingQuery, useAtributyQuery, useAkceQuery, useOpravneniQuery]}>
                    {() => {
                        const layerSettingData: LayerSettingsVO = useLayerSettingQuery.data;
                        const atributyData: AttributeConfigVO[] = useAtributyQuery.data;
                        const akceData: ActionConfigVO[] = useAkceQuery.data;
                        const opravneniData: UserRightsVO[] = useOpravneniQuery.data;
                        const userOpravneniData: UserRightsVO[] = opravneniData?.filter(right => {
                            return right.pracovnik?.pracovnikId === auth?.pracovnikId;
                        });

                        const existsDeleteAction = akceData?.some(akce => akce.typ === EnumActionType.DELETE);
                        const diskriminatorAtribut = atributyData.find(attribute => attribute.diskriminator === true);
                        const isUserVlastnikOrAdmin = !!(isLayerVlastnik(userOpravneniData?.[0], layer?.id) || isUserAdminOrMapioAdmin(auth));

                        const forbiddenAttributes = atributyData ? atributyData.map(i => i.popisek) : [];
                        const layerImage = layer?.layerImage ? layer?.layerImage : layer?.style?.marker;
                        return (
                            <GroupContainer
                                // Publikace jedné vrstvy a datum aktualizace jejích dat mají smysl jen u publikovaných vrstev (MCW-29207)
                                extra={
                                    layerSettingData.publikovat && (
                                        <ButtonToolbar>
                                            <LabelValue lastItem size={'small'} label="Data aktualizována k">
                                                {formatDate(useDataUpdatedQuery.data?.dataAktualizovanaK) || '-'}
                                            </LabelValue>
                                            {isUserVlastnikOrAdmin && (
                                                <Button inverse icon={<Icon icon={mcGlobe} />} onClick={handlePublishLayer}>
                                                    Publikovat ihned
                                                </Button>
                                            )}
                                        </ButtonToolbar>
                                    )
                                }
                                // @ts-ignore
                                title={
                                    <Title
                                        inverse
                                        intent={'inherit'}
                                        extraLeft
                                        extra={
                                            <ButtonToolbar>
                                                {layerSettingData.publikovat && <Tag intent={'success'}>Publikovat</Tag>}
                                                {isUserVlastnikOrAdmin && (
                                                    <Button onClick={() => handleUpdateLayer(layerSettingData)} style={'link'} icon={<Icon icon={mcEdit} />} />
                                                )}
                                            </ButtonToolbar>
                                        }
                                    >
                                        <div className={'d-flex align-items-center'}>
                                            {layerImage && (
                                                <img
                                                    className={'icon mr-1'}
                                                    src={
                                                        // Date.now() řeší problém s cache prohlížeče, kdy po změně ikony je v modálu zobrazena stále původní
                                                        `${appConfig.serverContextPath}/api/files/img/${layerImage}?t=${Date.now()}`
                                                    }
                                                    style={{maxWidth: 24, maxHeight: 24}}
                                                    alt={''}
                                                />
                                            )}
                                            {layer.label}
                                        </div>
                                    </Title>
                                }
                            >
                                <div className={'config-menu-overflow'}>
                                    <Row>
                                        <Col>
                                            <Card
                                                mode={'view'}
                                                title={'Atributy'}
                                                className={'h-100'}
                                                extra={
                                                    isUserVlastnikOrAdmin && (
                                                        <Button
                                                            style={'link'}
                                                            icon={<Icon icon={mcNew} />}
                                                            onClick={() => handleCreateUpdateAtribut(forbiddenAttributes)}
                                                        />
                                                    )
                                                }
                                            >
                                                <List>
                                                    {!atributyData || atributyData?.length === 0 ? (
                                                        <NoData />
                                                    ) : (
                                                        <>
                                                            {atributyData.map((atribut, index) => (
                                                                <ListItem
                                                                    key={`atribut-${atribut.id}`}
                                                                    extra={
                                                                        isUserVlastnikOrAdmin && (
                                                                            <ButtonToolbar>
                                                                                {atribut.vyhledavatelny && (
                                                                                    <Tooltip trigger={<Icon icon={mcSearch} intent={'primary'} />}>
                                                                                        Vyhledávání dle hodnoty atributu
                                                                                    </Tooltip>
                                                                                )}
                                                                                {atribut.filtrovatelny && (
                                                                                    <Tooltip trigger={<Icon icon={mcFilterOutline} intent={'primary'} />}>
                                                                                        Filtrování objektů v mapě dle hodnoty
                                                                                    </Tooltip>
                                                                                )}
                                                                                {atribut.publikovat && <Tag intent={'success'}>Publikovat</Tag>}
                                                                                {atribut.zobrazit && <Tag>Viditelný</Tag>}
                                                                                <Button
                                                                                    onClick={() => moveAtribut(atribut, atributyData[index - 1].poradi)}
                                                                                    style={'link'}
                                                                                    icon={<Icon icon={mcMoveUp} />}
                                                                                    disabled={index === 0}
                                                                                />
                                                                                <Button
                                                                                    onClick={() => moveAtribut(atribut, atributyData[index + 1].poradi)}
                                                                                    style={'link'}
                                                                                    icon={<Icon icon={mcMoveDown} />}
                                                                                    disabled={index === atributyData.length - 1}
                                                                                />
                                                                                <Button
                                                                                    style={'link'}
                                                                                    menu={
                                                                                        <Menu>
                                                                                            <MenuItem
                                                                                                onClick={() =>
                                                                                                    handleCreateUpdateAtribut(forbiddenAttributes, atribut)
                                                                                                }
                                                                                                text={
                                                                                                    <Text intent={'primary'} icon={<Icon icon={mcEdit} />}>
                                                                                                        Upravit atribut
                                                                                                    </Text>
                                                                                                }
                                                                                            />
                                                                                            {atribut.ciselnik && !atribut.diskriminator && muzeBytDiskriminator(atribut) && (
                                                                                                <MenuItem
                                                                                                    onClick={() => handleZapnoutOdliseniPodleAtributu(atribut)}
                                                                                                    text={
                                                                                                        <Text
                                                                                                            intent={'primary'}
                                                                                                            icon={<Icon icon={mcLocation} />}
                                                                                                        >
                                                                                                            Odlišit symboly podle hodnot atributu
                                                                                                        </Text>
                                                                                                    }
                                                                                                />
                                                                                            )}
                                                                                            {atribut.ciselnik && atribut.diskriminator && (
                                                                                                <MenuItem
                                                                                                    onClick={() => handleZrusitOdliseniPodleAtributu(atribut)}
                                                                                                    text={
                                                                                                        <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                                                                                            Vypnout odlišení podle hodnot atributu
                                                                                                        </Text>
                                                                                                    }
                                                                                                />
                                                                                            )}
                                                                                            <MenuDivider />
                                                                                            <MenuItem
                                                                                                onClick={() => handleRemoveAtribut(atribut)}
                                                                                                text={
                                                                                                    <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                                                                                        Smazat atribut
                                                                                                    </Text>
                                                                                                }
                                                                                            />
                                                                                        </Menu>
                                                                                    }
                                                                                />
                                                                            </ButtonToolbar>
                                                                        )
                                                                    }
                                                                >
                                                                    <Space className="d-flex align-items-center">
                                                                        <Title type="listItem" required={atribut.povinne || atribut.pouzitJakoLabel}>
                                                                            {atribut.popisek}
                                                                        </Title>
                                                                        <Text intent={'secondary'}>{getEnumTypLayerAttrReadable(atribut.typ)}</Text>
                                                                        {atribut.diskriminator && <Icon icon={mcLocation} />}
                                                                    </Space>
                                                                </ListItem>
                                                            ))}
                                                        </>
                                                    )}
                                                </List>
                                            </Card>
                                        </Col>
                                        <Col>
                                            <Card
                                                mode={'view'}
                                                title={'Akce'}
                                                className={'h-100'}
                                                extra={
                                                    isUserVlastnikOrAdmin && (
                                                        <Button
                                                            style={'link'}
                                                            icon={<Icon icon={mcNew} />}
                                                            menu={
                                                                <Menu>
                                                                    <div className={'p-1 px-2'}>
                                                                        <Text intent={'secondary'}>Vytvořit akci pro</Text>
                                                                    </div>
                                                                    <MenuItem
                                                                        onClick={() =>
                                                                            handleCreateUpdateAkce(
                                                                                {
                                                                                    typ: EnumActionType.INSERT,
                                                                                    polozky: [
                                                                                        {
                                                                                            skupina: {poradi: 0, nazev: 'Obecné údaje'},
                                                                                        },
                                                                                    ],
                                                                                },
                                                                                false,
                                                                                atributyData,
                                                                                diskriminatorAtribut,
                                                                            )
                                                                        }
                                                                        text={
                                                                            <Text intent={'primary'} icon={<Icon icon={mcNew} />}>
                                                                                Vytvoření objektu
                                                                            </Text>
                                                                        }
                                                                    />
                                                                    <MenuItem
                                                                        onClick={() =>
                                                                            handleCreateUpdateAkce(
                                                                                {
                                                                                    typ: EnumActionType.UPDATE,
                                                                                    polozky: [
                                                                                        {
                                                                                            skupina: {poradi: 0, nazev: 'Obecné údaje'},
                                                                                        },
                                                                                    ],
                                                                                },
                                                                                false,
                                                                                atributyData,
                                                                                diskriminatorAtribut,
                                                                            )
                                                                        }
                                                                        text={
                                                                            <Text intent={'primary'} icon={<Icon icon={mcEdit} />}>
                                                                                Změna objektu
                                                                            </Text>
                                                                        }
                                                                    />
                                                                    {!existsDeleteAction && (
                                                                        <MenuItem
                                                                            onClick={() =>
                                                                                handleCreateUpdateAkce(
                                                                                    {
                                                                                        typ: EnumActionType.DELETE,
                                                                                    },
                                                                                    false,
                                                                                    atributyData,
                                                                                    diskriminatorAtribut,
                                                                                )
                                                                            }
                                                                            text={
                                                                                <Text intent={'primary'} icon={<Icon icon={mcDelete} />}>
                                                                                    Odstranění objektu
                                                                                </Text>
                                                                            }
                                                                        />
                                                                    )}
                                                                </Menu>
                                                            }
                                                        />
                                                    )
                                                }
                                            >
                                                <List>
                                                    {!akceData || akceData?.length === 0 ? (
                                                        <NoData />
                                                    ) : (
                                                        <>
                                                            {akceData.map((akce, index) => (
                                                                <ListItem
                                                                    key={`akce-${akce.id}-${index}`}
                                                                    extra={
                                                                        isUserVlastnikOrAdmin && (
                                                                            <Button
                                                                                style={'link'}
                                                                                menu={
                                                                                    <Menu>
                                                                                        <MenuItem
                                                                                            onClick={() =>
                                                                                                handleCreateUpdateAkce(
                                                                                                    akce,
                                                                                                    true,
                                                                                                    atributyData,
                                                                                                    diskriminatorAtribut,
                                                                                                )
                                                                                            }
                                                                                            text={
                                                                                                <Text intent={'primary'} icon={<Icon icon={mcEdit} />}>
                                                                                                    Upravit akci
                                                                                                </Text>
                                                                                            }
                                                                                        />
                                                                                        <MenuDivider />
                                                                                        <MenuItem
                                                                                            onClick={() => handleRemoveAkce(akce)}
                                                                                            text={
                                                                                                <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                                                                                    Smazat akci
                                                                                                </Text>
                                                                                            }
                                                                                        />
                                                                                    </Menu>
                                                                                }
                                                                            />
                                                                        )
                                                                    }
                                                                >
                                                                    {akce.nazev}
                                                                </ListItem>
                                                            ))}
                                                        </>
                                                    )}
                                                </List>
                                            </Card>
                                        </Col>
                                    </Row>
                                    {diskriminatorAtribut && (
                                        <Row>
                                            <Col>
                                                <UserLayerAttributeSymbols
                                                    mapRef={mapRef}
                                                    topicId={topicId}
                                                    layer={layer}
                                                    atribut={diskriminatorAtribut}
                                                    isUserVlastnikOrAdmin={isUserVlastnikOrAdmin}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                    <Row>
                                        <Col>
                                            <OpravneniConfiguration
                                                opravneniItems={opravneniData}
                                                isUserVlastnikOrAdmin={isUserVlastnikOrAdmin}
                                                layer={layer}
                                                akce={akceData}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </GroupContainer>
                        );
                    }}
                </WaitForQueryData>
            )}
        </>
    );
};
